'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

// ── Types ──────────────────────────────────────────────────────────────────
interface HeroContent {
  badge: string;
  title: string;
  subtitle: string;
  cta: string;
  ctaNote: string;
  ctaSecondary: string;
  trust: string[];
}

interface HeroSectionProps {
  locale: 'fr' | 'en' | 'es';
  content: HeroContent;
}

// ── Delay helper ──────────────────────────────────────────────────────────
function delay(ms: number) {
  return new Promise<void>((res) => setTimeout(res, ms));
}

// ── Tab: Terminal Client (animated kiosk) ─────────────────────────────────
function TabTerminalClient() {
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [typed, setTyped] = useState('');
  const phone = '+1 (555) 012-3456';

  useEffect(() => {
    let cancelled = false;
    async function sequence() {
      if (cancelled) return;
      setStep(0); setTyped('');
      await delay(1200);
      if (cancelled) return;
      setStep(1);
      for (let i = 0; i <= phone.length; i++) {
        if (cancelled) return;
        setTyped(phone.slice(0, i));
        await delay(60);
      }
      await delay(600);
      if (cancelled) return;
      setStep(2);
      await delay(2400);
      if (cancelled) return;
      setStep(3);
      await delay(2800);
    }
    sequence();
    const loop = setInterval(() => { if (!cancelled) sequence(); }, 9000);
    return () => { cancelled = true; clearInterval(loop); };
  }, []);

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--lz-deep)', borderRadius: '0 0 16px 16px', overflow: 'hidden' }}>
      {/* App chrome bar */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ borderBottom: '1px solid var(--lz-border)', background: 'var(--lz-card)' }}
      >
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
            <div className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
            <div className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
          </div>
          <span className="lz-mono" style={{ fontSize: '11px', color: 'var(--lz-text-secondary)' }}>Terminal · Loyzen</span>
        </div>
        <span
          className="text-[9px] px-2 py-0.5 rounded-full lz-label"
          style={{ background: 'rgba(112,245,154,0.1)', color: 'var(--lz-green)', border: '1px solid rgba(112,245,154,0.2)' }}
        >
          Démonstration
        </span>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar */}
        <div
          className="flex flex-col py-4 px-3 gap-1"
          style={{ width: '160px', borderRight: '1px solid var(--lz-border)', background: 'var(--lz-card)', flexShrink: 0 }}
        >
          <div className="flex items-center gap-2 px-2 py-2 mb-3">
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center"
              style={{ background: 'rgba(112,245,154,0.15)', border: '1px solid rgba(112,245,154,0.25)' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2" strokeLinecap="round">
                <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </div>
            <span className="lz-mono" style={{ fontSize: '11px', color: 'var(--lz-text-primary)' }}>Loyzen</span>
          </div>
          {[
            { icon: 'M12 2a10 10 0 100 20A10 10 0 0012 2z', label: 'Accueil', active: true },
            { icon: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z', label: 'Mon compte', active: false },
            { icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z', label: 'Récompenses', active: false },
            { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', label: 'Historique', active: false },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-2 py-2 rounded-lg"
              style={{
                background: item.active ? 'rgba(255,255,255,0.06)' : 'transparent',
                cursor: 'default',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={item.active ? 'var(--lz-green)' : 'var(--lz-text-secondary)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d={item.icon} />
              </svg>
              <span className="lz-body" style={{ fontSize: '11px', color: item.active ? 'var(--lz-text-primary)' : 'var(--lz-text-secondary)' }}>{item.label}</span>
            </div>
          ))}
          <div className="mt-auto pt-4">
            <div
              className="flex items-center gap-2 px-2 py-2 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.15)' }}>
                <span className="lz-mono" style={{ fontSize: '8px', color: 'var(--lz-green)' }}>M</span>
              </div>
              <span className="lz-body" style={{ fontSize: '10px', color: 'var(--lz-text-secondary)' }}>marie@…</span>
            </div>
          </div>
        </div>

        {/* Main area */}
        <div className="flex-1 flex flex-col p-5 gap-4 overflow-hidden">
          {/* Step 0 — idle */}
          {step === 0 && (
            <div className="flex flex-col items-center justify-center flex-1 gap-4">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: 'rgba(112,245,154,0.08)', border: '1px solid rgba(112,245,154,0.15)' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="1.6" strokeLinecap="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
                </svg>
              </div>
              <div className="text-center">
                <p className="lz-body" style={{ fontSize: '14px', color: 'var(--lz-text-primary)', marginBottom: '4px' }}>Bienvenue</p>
                <p className="lz-body" style={{ fontSize: '12px', color: 'var(--lz-text-secondary)' }}>Saisissez votre numéro pour consulter vos points</p>
              </div>
            </div>
          )}

          {/* Step 1 — typing */}
          {step === 1 && (
            <div className="flex flex-col justify-center flex-1 gap-4 max-w-xs mx-auto w-full">
              <div>
                <p className="lz-label" style={{ fontSize: '10px', marginBottom: '8px' }}>Votre numéro de téléphone</p>
                <div
                  className="rounded-xl px-4 py-3 lz-mono"
                  style={{
                    background: 'var(--lz-raised)',
                    border: '1px solid rgba(112,245,154,0.3)',
                    fontSize: '16px',
                    minHeight: '48px',
                    letterSpacing: '0.04em',
                    boxShadow: '0 0 0 3px rgba(112,245,154,0.06)',
                  }}
                >
                  {typed}<span className="animate-pulse" style={{ opacity: 0.7 }}>|</span>
                </div>
                <p className="lz-body" style={{ fontSize: '10px', color: 'var(--lz-text-secondary)', marginTop: '6px' }}>Format international · E.164</p>
              </div>
            </div>
          )}

          {/* Step 2 — points dashboard */}
          {step === 2 && (
            <div className="flex flex-col gap-4 flex-1">
              {/* Client identified */}
              <div className="flex items-center gap-3 pb-3" style={{ borderBottom: '1px solid var(--lz-border)' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.1)', border: '1px solid rgba(112,245,154,0.2)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2" strokeLinecap="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                  </svg>
                </div>
                <div>
                  <p className="lz-body" style={{ fontSize: '11px', color: 'var(--lz-text-primary)' }}>Client identifié</p>
                  <p className="lz-mono" style={{ fontSize: '10px', color: 'var(--lz-text-secondary)' }}>+1 (555) 012-3456</p>
                </div>
                <div className="ml-auto">
                  <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(112,245,154,0.1)', color: 'var(--lz-green)', border: '1px solid rgba(112,245,154,0.2)' }}>Actif</span>
                </div>
              </div>

              {/* KPI row */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Points', value: '340', sub: 'solde actuel' },
                  { label: 'Récompense', value: '10 €', sub: 'disponible' },
                  { label: 'Palier', value: '68%', sub: '340 / 500' },
                ].map((kpi, i) => (
                  <div key={i} className="rounded-xl p-3" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                    <p className="lz-label" style={{ fontSize: '8px', marginBottom: '4px' }}>{kpi.label}</p>
                    <p className="lz-mono" style={{ fontSize: i === 0 ? '20px' : '14px', lineHeight: 1.1, color: i === 1 ? 'var(--lz-green-bright)' : 'var(--lz-text-primary)' }}>{kpi.value}</p>
                    <p className="lz-body" style={{ fontSize: '9px', color: 'var(--lz-text-secondary)', marginTop: '2px' }}>{kpi.sub}</p>
                  </div>
                ))}
              </div>

              {/* Progress bar */}
              <div className="rounded-xl p-3" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                <div className="flex justify-between mb-2">
                  <span className="lz-body" style={{ fontSize: '10px', color: 'var(--lz-text-secondary)' }}>Prochain palier</span>
                  <span className="lz-mono" style={{ fontSize: '10px' }}>340 / 500 pts</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: 'var(--lz-deep)' }}>
                  <div className="h-1.5 rounded-full" style={{ width: '68%', background: 'linear-gradient(90deg, var(--lz-green), var(--lz-green-bright))', transition: 'width 1s ease' }} />
                </div>
                <p className="lz-body" style={{ fontSize: '9px', color: 'var(--lz-text-secondary)', marginTop: '6px' }}>160 pts pour débloquer la prochaine récompense</p>
              </div>
            </div>
          )}

          {/* Step 3 — reward confirmed */}
          {step === 3 && (
            <div className="flex flex-col items-center justify-center flex-1 gap-4">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: 'rgba(112,245,154,0.12)', border: '1px solid rgba(112,245,154,0.3)' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="text-center">
                <p className="lz-body" style={{ fontSize: '14px', color: 'var(--lz-text-primary)', marginBottom: '4px' }}>Récompense activée</p>
                <p className="lz-mono" style={{ fontSize: '24px', color: 'var(--lz-green)', marginBottom: '4px' }}>+10 €</p>
                <p className="lz-body" style={{ fontSize: '11px', color: 'var(--lz-text-secondary)' }}>Le vendeur valide la vente côté caisse</p>
              </div>
              <div className="rounded-xl px-4 py-2" style={{ background: 'rgba(112,245,154,0.07)', border: '1px solid rgba(112,245,154,0.15)' }}>
                <p className="lz-mono" style={{ fontSize: '11px', color: 'var(--lz-green)' }}>+47 pts ajoutés · Solde : 387 pts</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tab: Caisse Vendeur (POS) ─────────────────────────────────────────────
function TabCaisseVendeur() {
  const [posStep, setPosStep] = useState<0 | 1 | 2 | 3>(0);
  const [amount, setAmount] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function sequence() {
      if (cancelled) return;
      setPosStep(0); setAmount('');
      await delay(1000);
      if (cancelled) return;
      setPosStep(1);
      const digits = '47.50';
      for (let i = 0; i <= digits.length; i++) {
        if (cancelled) return;
        setAmount(digits.slice(0, i));
        await delay(120);
      }
      await delay(800);
      if (cancelled) return;
      setPosStep(2);
      await delay(2500);
      if (cancelled) return;
      setPosStep(3);
      await delay(3000);
    }
    sequence();
    const loop = setInterval(() => { if (!cancelled) sequence(); }, 10000);
    return () => { cancelled = true; clearInterval(loop); };
  }, []);

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--lz-deep)', borderRadius: '0 0 16px 16px', overflow: 'hidden' }}>
      {/* App chrome bar */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ borderBottom: '1px solid var(--lz-border)', background: 'var(--lz-card)' }}
      >
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
            <div className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
            <div className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
          </div>
          <span className="lz-mono" style={{ fontSize: '11px', color: 'var(--lz-text-secondary)' }}>Caisse · Loyzen POS</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] px-2 py-0.5 rounded-full lz-label" style={{ background: 'rgba(112,245,154,0.1)', color: 'var(--lz-green)', border: '1px solid rgba(112,245,154,0.2)' }}>PIN ✓</span>
          <span className="text-[9px] px-2 py-0.5 rounded-full lz-label" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--lz-text-secondary)', border: '1px solid var(--lz-border)' }}>Démonstration</span>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div
          className="flex flex-col py-4 px-3 gap-1"
          style={{ width: '160px', borderRight: '1px solid var(--lz-border)', background: 'var(--lz-card)', flexShrink: 0 }}
        >
          <div className="flex items-center gap-2 px-2 py-2 mb-3">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.15)', border: '1px solid rgba(112,245,154,0.25)' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2" strokeLinecap="round">
                <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </div>
            <span className="lz-mono" style={{ fontSize: '11px', color: 'var(--lz-text-primary)' }}>Loyzen</span>
          </div>
          {[
            { icon: 'M3 3h18v18H3zM3 9h18M9 21V9', label: 'Vente', active: true },
            { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', label: 'Transactions', active: false },
            { icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75', label: 'Clients', active: false },
            { icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z', label: 'Récompenses', active: false },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 px-2 py-2 rounded-lg" style={{ background: item.active ? 'rgba(255,255,255,0.06)' : 'transparent', cursor: 'default' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={item.active ? 'var(--lz-green)' : 'var(--lz-text-secondary)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d={item.icon} />
              </svg>
              <span className="lz-body" style={{ fontSize: '11px', color: item.active ? 'var(--lz-text-primary)' : 'var(--lz-text-secondary)' }}>{item.label}</span>
            </div>
          ))}
          <div className="mt-auto pt-4">
            <div className="flex items-center gap-2 px-2 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.15)' }}>
                <span className="lz-mono" style={{ fontSize: '8px', color: 'var(--lz-green)' }}>S</span>
              </div>
              <span className="lz-body" style={{ fontSize: '10px', color: 'var(--lz-text-secondary)' }}>Sophie · PIN</span>
            </div>
          </div>
        </div>

        {/* Main POS area */}
        <div className="flex-1 flex flex-col p-5 gap-4 overflow-hidden">
          {/* Step 0 — auth */}
          {posStep === 0 && (
            <div className="flex flex-col items-center justify-center flex-1 gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.08)', border: '1px solid rgba(112,245,154,0.15)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="1.8" strokeLinecap="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              </div>
              <p className="lz-body" style={{ fontSize: '13px', color: 'var(--lz-text-secondary)' }}>Authentification vendeur…</p>
              <div className="flex gap-2">
                {[0,1,2,3].map(i => (
                  <div key={i} className="w-3 h-3 rounded-full" style={{ background: i < 2 ? 'var(--lz-green)' : 'var(--lz-border)' }} />
                ))}
              </div>
            </div>
          )}

          {/* Step 1 — amount entry */}
          {posStep === 1 && (
            <div className="flex flex-col gap-4 flex-1">
              <div className="flex items-center gap-3 pb-3" style={{ borderBottom: '1px solid var(--lz-border)' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.1)', border: '1px solid rgba(112,245,154,0.2)' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2" strokeLinecap="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                  </svg>
                </div>
                <div>
                  <p className="lz-body" style={{ fontSize: '11px', color: 'var(--lz-text-primary)' }}>Client identifié</p>
                  <p className="lz-mono" style={{ fontSize: '10px', color: 'var(--lz-text-secondary)' }}>+1 (555) 012-3456</p>
                </div>
              </div>
              <div>
                <p className="lz-label" style={{ fontSize: '9px', marginBottom: '6px' }}>Montant de la vente</p>
                <div className="rounded-xl px-4 py-3 flex items-center gap-2" style={{ background: 'var(--lz-raised)', border: '1px solid rgba(112,245,154,0.3)', boxShadow: '0 0 0 3px rgba(112,245,154,0.06)' }}>
                  <span className="lz-mono" style={{ fontSize: '28px', color: 'var(--lz-text-primary)' }}>{amount || '0'}</span>
                  <span className="lz-mono" style={{ fontSize: '18px', color: 'var(--lz-text-secondary)' }}>€</span>
                  <span className="animate-pulse lz-mono" style={{ fontSize: '24px', color: 'var(--lz-green)', marginLeft: '2px' }}>|</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — review + reward */}
          {posStep === 2 && (
            <div className="flex flex-col gap-3 flex-1">
              <div className="flex items-center gap-3 pb-3" style={{ borderBottom: '1px solid var(--lz-border)' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.1)', border: '1px solid rgba(112,245,154,0.2)' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2" strokeLinecap="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                  </svg>
                </div>
                <div>
                  <p className="lz-body" style={{ fontSize: '11px', color: 'var(--lz-text-primary)' }}>Client identifié · 340 pts</p>
                  <p className="lz-mono" style={{ fontSize: '10px', color: 'var(--lz-text-secondary)' }}>+1 (555) 012-3456</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-3" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                  <p className="lz-label" style={{ fontSize: '8px', marginBottom: '4px' }}>Montant</p>
                  <p className="lz-mono" style={{ fontSize: '22px', lineHeight: 1 }}>47,50 €</p>
                </div>
                <div className="rounded-xl p-3" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                  <p className="lz-label" style={{ fontSize: '8px', marginBottom: '4px' }}>Points gagnés</p>
                  <p className="lz-mono" style={{ fontSize: '22px', lineHeight: 1, color: 'var(--lz-green)' }}>+47</p>
                </div>
              </div>
              <div className="rounded-xl p-3 flex items-center justify-between" style={{ background: 'rgba(112,245,154,0.07)', border: '1px solid rgba(112,245,154,0.2)' }}>
                <div>
                  <p className="lz-label" style={{ fontSize: '8px', marginBottom: '2px' }}>Récompense disponible</p>
                  <p className="lz-mono" style={{ fontSize: '13px', color: 'var(--lz-green)' }}>10 € de remise</p>
                </div>
                <div className="rounded-lg px-3 py-1.5" style={{ background: 'rgba(112,245,154,0.15)', border: '1px solid rgba(112,245,154,0.3)', cursor: 'default' }}>
                  <span className="lz-mono" style={{ fontSize: '10px', color: 'var(--lz-green)' }}>Appliquer</span>
                </div>
              </div>
              <button
                className="w-full rounded-xl py-3 font-semibold mt-auto"
                style={{ background: 'var(--lz-green)', color: '#050505', fontFamily: 'var(--font-dm-sans)', fontSize: '13px' }}
              >
                Valider la vente
              </button>
            </div>
          )}

          {/* Step 3 — confirmed */}
          {posStep === 3 && (
            <div className="flex flex-col items-center justify-center flex-1 gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.12)', border: '1px solid rgba(112,245,154,0.3)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="text-center">
                <p className="lz-body" style={{ fontSize: '14px', color: 'var(--lz-text-primary)', marginBottom: '4px' }}>Vente validée</p>
                <p className="lz-mono" style={{ fontSize: '22px', color: 'var(--lz-text-primary)', marginBottom: '4px' }}>47,50 €</p>
                <p className="lz-body" style={{ fontSize: '11px', color: 'var(--lz-text-secondary)' }}>+47 pts · Récompense 10 € appliquée</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tab: Tableau de bord (Owner Dashboard) ────────────────────────────────
function TabTableauDeBord() {
  const chartData = [
    { day: 'L', val: 42 }, { day: 'M', val: 68 }, { day: 'M', val: 55 },
    { day: 'J', val: 89 }, { day: 'V', val: 112 }, { day: 'S', val: 134 }, { day: 'D', val: 78 },
  ];
  const maxVal = 134;

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--lz-deep)', borderRadius: '0 0 16px 16px', overflow: 'hidden' }}>
      {/* App chrome bar */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ borderBottom: '1px solid var(--lz-border)', background: 'var(--lz-card)' }}
      >
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
            <div className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
            <div className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
          </div>
          <span className="lz-mono" style={{ fontSize: '11px', color: 'var(--lz-text-secondary)' }}>Tableau de bord · Loyzen</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] px-2 py-0.5 rounded-full lz-label" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--lz-text-secondary)', border: '1px solid var(--lz-border)' }}>Aide</span>
          <span className="text-[9px] px-2 py-0.5 rounded-full lz-label" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--lz-text-secondary)', border: '1px solid var(--lz-border)' }}>Démonstration</span>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div
          className="flex flex-col py-4 px-3 gap-1"
          style={{ width: '160px', borderRight: '1px solid var(--lz-border)', background: 'var(--lz-card)', flexShrink: 0 }}
        >
          <div className="flex items-center gap-2 px-2 py-2 mb-3">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.15)', border: '1px solid rgba(112,245,154,0.25)' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2" strokeLinecap="round">
                <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </div>
            <span className="lz-mono" style={{ fontSize: '11px', color: 'var(--lz-text-primary)' }}>Loyzen</span>
          </div>
          {[
            { icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', label: 'Tableau de bord', active: true },
            { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', label: 'Transactions', active: false },
            { icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75', label: 'Clients', active: false },
            { icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z', label: 'Récompenses', active: false },
            { icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', label: 'Équipe', active: false },
            { icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', label: 'Paramètres', active: false },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 px-2 py-2 rounded-lg" style={{ background: item.active ? 'rgba(255,255,255,0.06)' : 'transparent', cursor: 'default' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={item.active ? 'var(--lz-green)' : 'var(--lz-text-secondary)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d={item.icon} />
              </svg>
              <span className="lz-body" style={{ fontSize: '11px', color: item.active ? 'var(--lz-text-primary)' : 'var(--lz-text-secondary)' }}>{item.label}</span>
            </div>
          ))}
          <div className="mt-auto pt-4">
            <div className="flex items-center gap-2 px-2 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.15)' }}>
                <span className="lz-mono" style={{ fontSize: '8px', color: 'var(--lz-green)' }}>Y</span>
              </div>
              <span className="lz-body" style={{ fontSize: '10px', color: 'var(--lz-text-secondary)' }}>you@domain…</span>
            </div>
          </div>
        </div>

        {/* Dashboard main */}
        <div className="flex-1 flex flex-col p-5 gap-4 overflow-hidden">
          <div className="flex items-center justify-between">
            <p className="lz-h2" style={{ fontSize: '16px' }}>Métriques</p>
            <span className="lz-body" style={{ fontSize: '10px', color: 'var(--lz-text-secondary)' }}>7 derniers jours</span>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'FIDÉLISATION', status: 'Bonne', statusColor: '#70F59A', items: [{ dot: '#3b82f6', name: 'Transactions', val: '1 247', pct: '100%' }, { dot: '#70F59A', name: 'Fidélisées', val: '1 247', pct: '100%' }] },
              { label: 'RÉCOMPENSES', status: 'Active', statusColor: '#70F59A', items: [{ dot: '#ef4444', name: 'Utilisées', val: '89', pct: '7.1%' }, { dot: '#f59e0b', name: 'Expirées', val: '2', pct: '0.2%' }] },
              { label: 'ENGAGEMENT', status: 'Faible', statusColor: '#f59e0b', items: [{ dot: '#a78bfa', name: 'Retours', val: '0', pct: '0%' }, { dot: '#6b7280', name: 'Inactifs', val: '137', pct: '11%' }] },
            ].map((card, i) => (
              <div key={i} className="rounded-xl p-3 flex flex-col gap-2" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                <p className="lz-label" style={{ fontSize: '8px', color: 'var(--lz-text-secondary)', letterSpacing: '0.08em' }}>{card.label}</p>
                <p className="lz-h2" style={{ fontSize: '18px', color: card.statusColor }}>{card.status}</p>
                <div className="flex flex-col gap-1.5 mt-1">
                  {card.items.map((item, j) => (
                    <div key={j} className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: item.dot }} />
                        <span className="lz-body" style={{ fontSize: '9px', color: 'var(--lz-text-secondary)' }}>{item.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="lz-mono" style={{ fontSize: '9px', color: 'var(--lz-text-secondary)' }}>{item.val}</span>
                        <span className="lz-mono" style={{ fontSize: '9px', color: 'var(--lz-text-primary)', fontWeight: 600 }}>{item.pct}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="rounded-xl p-4 flex-1" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)', minHeight: 0 }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="lz-label" style={{ fontSize: '8px', color: 'var(--lz-text-secondary)', letterSpacing: '0.08em', marginBottom: '2px' }}>TRANSACTIONS FIDÉLISÉES</p>
                <p className="lz-mono" style={{ fontSize: '22px', lineHeight: 1 }}>1,247</p>
              </div>
              <div className="flex items-center gap-1 rounded-lg px-2 py-1" style={{ background: 'rgba(112,245,154,0.08)', border: '1px solid rgba(112,245,154,0.15)' }}>
                <span className="lz-mono" style={{ fontSize: '9px', color: 'var(--lz-green)' }}>↑ 12%</span>
              </div>
            </div>
            {/* Bar chart */}
            <div className="flex items-end gap-2 h-16">
              {chartData.map((d, i) => (
                <div key={i} className="flex flex-col items-center gap-1 flex-1">
                  <div
                    className="w-full rounded-t-sm"
                    style={{
                      height: `${(d.val / maxVal) * 52}px`,
                      background: i === 5 ? 'var(--lz-green)' : 'rgba(112,245,154,0.2)',
                      transition: 'height 0.6s ease',
                    }}
                  />
                  <span className="lz-mono" style={{ fontSize: '8px', color: 'var(--lz-text-secondary)' }}>{d.day}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab definitions ───────────────────────────────────────────────────────
const TABS = [
  {
    id: 'terminal',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
    label: 'Terminal client',
    component: TabTerminalClient,
  },
  {
    id: 'caisse',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /><path d="M7 8h.01M11 8h.01M15 8h.01M7 12h.01M11 12h.01M15 12h.01" />
      </svg>
    ),
    label: 'Caisse vendeur',
    component: TabCaisseVendeur,
  },
  {
    id: 'dashboard',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
      </svg>
    ),
    label: 'Tableau de bord',
    component: TabTableauDeBord,
  },
];

// ── Demo Showcase (Resend-style) ──────────────────────────────────────────
function DemoShowcase() {
  const [activeTab, setActiveTab] = useState('terminal');
  const ActiveComponent = TABS.find(t => t.id === activeTab)?.component ?? TabTerminalClient;

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Tab buttons row — Resend style */}
      <div className="grid grid-cols-3 gap-3">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-3 px-4 py-4 rounded-xl text-left transition-all duration-200"
              style={{
                background: isActive ? 'var(--lz-raised)' : 'var(--lz-card)',
                border: isActive
                  ? '1px solid rgba(112,245,154,0.25)'
                  : '1px solid var(--lz-border)',
                cursor: 'pointer',
                boxShadow: isActive ? '0 0 0 1px rgba(112,245,154,0.08)' : 'none',
              }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  background: isActive ? 'rgba(112,245,154,0.12)' : 'rgba(255,255,255,0.04)',
                  border: isActive ? '1px solid rgba(112,245,154,0.2)' : '1px solid var(--lz-border)',
                  color: isActive ? 'var(--lz-green)' : 'var(--lz-text-secondary)',
                  transition: 'all 0.2s',
                }}
              >
                {tab.icon}
              </div>
              <span
                className="lz-body"
                style={{
                  fontSize: '12px',
                  fontWeight: isActive ? 500 : 400,
                  color: isActive ? 'var(--lz-text-primary)' : 'var(--lz-text-secondary)',
                  transition: 'color 0.2s',
                  lineHeight: 1.3,
                }}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Dashboard panel — full width */}
      <div
        className="w-full overflow-hidden"
        style={{
          background: 'var(--lz-card)',
          border: '1px solid var(--lz-border)',
          borderRadius: '16px',
          height: '420px',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03)',
        }}
      >
        <ActiveComponent />
      </div>
    </div>
  );
}

// ── Main HeroSection ──────────────────────────────────────────────────────
export default function HeroSection({ locale, content }: HeroSectionProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;

  return (
    <section
      className="relative overflow-hidden"
      style={{
        minHeight: '92vh',
        background: 'var(--lz-bg)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {/* ── Background layers ──────────────────────────────────────────── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          top: '55%',
          right: '10%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(ellipse, rgba(112,245,154,0.055) 0%, transparent 65%)',
          filter: 'blur(60px)',
          transform: 'translateY(-50%)',
        }}
      />

      {/* ── Content ────────────────────────────────────────────────────── */}
      <div
        className="relative z-10 w-full"
        style={{
          maxWidth: '1360px',
          margin: '0 auto',
          padding: 'clamp(96px, 10vw, 140px) clamp(24px, 4vw, 48px) clamp(64px, 8vw, 100px)',
        }}
      >
        {/* ── Desktop layout: left text / right visuals ──────────────── */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-16 xl:gap-20">

          {/* Left column — editorial text */}
          <div className="flex-shrink-0 flex flex-col" style={{ maxWidth: '460px' }}>
            {/* Badge */}
            <div
              className="inline-flex self-start items-center gap-2 px-3.5 py-1.5 rounded-full mb-10"
              style={{
                background: 'rgba(112,245,154,0.07)',
                border: '1px solid rgba(112,245,154,0.18)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'var(--lz-green)' }} />
              <span className="lz-label" style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'none', fontWeight: 500 }}>
                {content.badge}
              </span>
            </div>

            {/* H1 */}
            <h1 className="lz-h1 whitespace-pre-line mb-8" style={{ maxWidth: '440px' }}>
              {content.title}
            </h1>

            {/* Subtitle */}
            <p className="lz-body mb-10" style={{ maxWidth: '420px', fontSize: 'clamp(15px, 1.4vw, 18px)', lineHeight: 1.65 }}>
              {content.subtitle}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-3 mb-5">
              <Link
                href="/signup"
                className="inline-flex items-center px-7 py-3.5 rounded-xl text-[15px] font-semibold transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                style={{
                  background: 'var(--lz-green)',
                  color: '#050505',
                  fontFamily: 'var(--font-dm-sans)',
                  boxShadow: '0 0 0 1px rgba(112,245,154,0.25), 0 4px 16px rgba(112,245,154,0.15)',
                }}
              >
                {content.cta}
              </Link>
              <Link
                href="/how-it-works"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-[15px] font-normal transition-all duration-200 hover:text-white"
                style={{
                  color: 'var(--lz-text-secondary)',
                  border: '1px solid var(--lz-border)',
                  background: 'rgba(255,255,255,0.02)',
                  fontFamily: 'var(--font-dm-sans)',
                }}
              >
                {content.ctaSecondary}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </Link>
            </div>

            {/* Pricing note */}
            <p className="lz-mono mb-10" style={{ fontSize: '12px', color: 'var(--lz-text-secondary)' }}>
              {content.ctaNote}
            </p>

            {/* Trust ribbon */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {content.trust.map((item, i) => (
                <div key={i} className="flex items-center gap-1.5" style={{ fontFamily: 'var(--font-dm-sans)', fontSize: '12px', color: '#555955' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Right column — Resend-style demo showcase */}
          <div className="hidden lg:flex flex-1 flex-col" style={{ minWidth: 0 }}>
            <DemoShowcase />
          </div>
        </div>

        {/* ── Mobile visual — stacked ──────────────────────────────────── */}
        <div className="flex lg:hidden flex-col mt-14">
          <DemoShowcase />
        </div>
      </div>
    </section>
  );
}
